import { AnalyzeExcelRequest, AnalyzeExcelResponse, ConfirmBatchRequest, ConfirmBatchResponse } from "@/pages/conciliation/analyst-assignment/types/excel-upload.types";
import { IBalanceReport } from "src/@types/balance-report";
import { JobProgressResponseDto } from "src/@types/job-progress";
import { IGetLateParams } from "src/@types/late-conciliation";
import { Paginated } from "src/@types/paginated";
import {
  IGetStoresParams,
  IPayment,
  ISelectResponseById,
  IUpdateStoreResponsible,
} from "src/@types/stores";
import { Tags } from "../../enum/tags";
import { apiSlice } from "../http";

export const storeInformations = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getStores: builder.query<Paginated<IPayment>, IGetStoresParams>({
      query: (filters) => {
        const params = new URLSearchParams(
          filters as unknown as Record<string, string>
        );

        return {
          url: `/store/get-transactions?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.STORE],
    }),
    getLateStores: builder.query<Paginated<IPayment>, IGetLateParams>({
      query: (filters) => {
        const params = new URLSearchParams(
          filters as unknown as Record<string, string>
        );

        return {
          url: `/store/get-late-transactions?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.LATE_TRANSACTIONS],
    }),
    getFilteredStores: builder.query<Paginated<IPayment>, IGetStoresParams>({
      query: (filters) => {
        const params = new URLSearchParams(
          filters as unknown as Record<string, string>
        );

        return {
          url: `/store/get-stores?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.STORE],
    }),
    getUniqueStore: builder.query<
      ISelectResponseById,
      { idStore: string; idTransaction: string }
    >({
      query: ({ idStore, idTransaction }) => {
        return {
          url: `/store/${idStore}/transations/${idTransaction}`,
          method: "GET",
        };
      },
      providesTags: [Tags.OPTIONS_REGIONAL],
    }),
    updateStoreResponsible: builder.mutation<string, IUpdateStoreResponsible[]>(
      {
        query: (data: IUpdateStoreResponsible[]) => ({
          url: `/store/assignment/batch`,
          method: "PUT",
          body: data,
        }),
        invalidatesTags: [Tags.STORE, Tags.STORE_CONCILIATION],
      }
    ),
    getTransactionByDate: builder.query<
      Paginated<IBalanceReport>,
      IGetStoresParams
    >({
      query: (filters) => {
        const params = new URLSearchParams(
          filters as unknown as Record<string, string>
        );

        return {
          url: `/store/get-transactions-by-date?${params.toString()}`,
          method: "GET",
        };
      },

      providesTags: [Tags.TRANSACTION_BY_DATE],
    }),
    getUserStores: builder.query<{ id: string }, { id?: string }>({
      query: ({ id }) => {
        return {
          url: `/store/user/${id}`,
          method: "GET",
        };
      },
      providesTags: [Tags.STORE],
    }),
    exportTransactionByDateExcel: builder.mutation<{ excelFile: string }, IGetStoresParams>({
      query: (filters) => {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              value.forEach(v => params.append(`${key}[]`, v.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });

        return {
          url: `/store/get-transactions-by-date/export?${params.toString()}`,
          method: "GET",
          timeout: 120000, // 2 minutos para exportação
        };
      },
    }),
    getJobProgress: builder.query<JobProgressResponseDto, void>({
      query: () => ({
        url: `/store/progress`,
        method: "GET",
      }),
      providesTags: [Tags.JOB_PROGRESS],
    }),

    retryStore: builder.mutation<
      { retried: boolean; message: string },
      { storeCode: number; date: string }
    >({
      query: (body) => ({
        url: `/store/job/retry-store`,
        method: "POST",
        body,
      }),
      invalidatesTags: [Tags.JOB_PROGRESS],
    }),

    forceResetJobProgress: builder.mutation<{ message: string; queueCleared: boolean }, void>({
      query: () => ({
        url: `/store/progress/reset`,
        method: "DELETE",
      }),
      invalidatesTags: [Tags.JOB_PROGRESS],
    }),

    analyzeExcel: builder.mutation<AnalyzeExcelResponse, AnalyzeExcelRequest>({
      query: (body) => ({
        url: "/store/assignment/analyze-excel",
        method: "POST",
        body,
      }),
    }),

    confirmBatch: builder.mutation<ConfirmBatchResponse, ConfirmBatchRequest>({
      query: (body) => ({
        url: "/store/assignment/confirm-batch",
        method: "POST",
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          console.log("Backend completou - invalidando cache");
          dispatch(
            storeInformations.util.invalidateTags([Tags.STORE, Tags.OPTIONS_REGIONAL])
          );
        } catch (error) {
          console.error("Erro - não invalidando cache");
        }
      },
    }),

    downloadTemplate: builder.mutation<Blob, void>({
      query: () => ({
        url: "/store/template/download",
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    exportLateReconciliationsExcel: builder.mutation<Blob, IGetLateParams>({
      query: (filters) => {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              value.forEach(v => params.append(`${key}[]`, v.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });

        return {
          url: `/store/get-late-transactions/export-excel?${params.toString()}`,
          method: "POST",
          responseHandler: async (response: { blob: () => any; }) => {
            const blob = await response.blob();
            return blob;
          },
          cache: "no-cache",
        };
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetStoresQuery,
  useGetLateStoresQuery,
  useGetFilteredStoresQuery,
  useUpdateStoreResponsibleMutation,
  useGetTransactionByDateQuery,
  useGetUserStoresQuery,
  useExportTransactionByDateExcelMutation,
  useGetJobProgressQuery,
  useLazyGetJobProgressQuery,
  useRetryStoreMutation,
  useForceResetJobProgressMutation,
  useAnalyzeExcelMutation,
  useConfirmBatchMutation,
  useDownloadTemplateMutation,
  useExportLateReconciliationsExcelMutation,
} = storeInformations;

export const {
  endpoints: {
    getStores: { select: selectGetStores },
    getLateStores: { select: selectGetLateStores },
    getFilteredStores: { select: selectGetFilteredStores },
    updateStoreResponsible: { select: selectUpdateStore },
    getTransactionByDate: { select: selectGetTransactionByDate },
    getUserStores: { select: selectGetUserStores },
    analyzeExcel: { select: selectAnalyzeExcel },
    confirmBatch: { select: selectConfirmBatch },
    downloadTemplate: { select: selectDownloadTemplate }

  },
} = storeInformations;
